import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Navbar from '../../components/Navbar';
import { PageHeader } from '../../components/ui/page-header';
import { DoorOpen, DoorClosed, Loader2, Store } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtTime = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); }
  catch { return '—'; }
};

const AdminCounters = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/counters`, { withCredentials: true });
      setRows(data);
    } catch (e) { setError(e?.response?.data?.detail || 'Failed to load counters'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async (c) => {
    setBusy(c.id); setError('');
    const status = c.status === 'open' ? 'closed' : 'open';
    try {
      await axios.patch(`${API}/counters/${c.id}`, { status }, { withCredentials: true });
      await load();
    } catch (e) { setError(e?.response?.data?.detail || 'Could not update counter'); }
    finally { setBusy(null); }
  };

  const openCount = rows.filter((r) => r.status === 'open').length;

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-background">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8" data-testid="admin-counters-page">
          <PageHeader
            title="Counters"
            subtitle={`View and manage every outlet's counter status. ${openCount}/${rows.length} open.`}
            icon={Store}
          />
          {error && <div data-testid="admin-counters-error" className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">{error}</div>}
          {loading ? (
            <div className="py-16 text-center text-text-muted"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
          ) : rows.length === 0 ? (
            <div className="bg-card border border-border-light rounded-2xl p-10 text-center text-text-muted" data-testid="admin-counters-empty">
              No counters found in your scope yet.
            </div>
          ) : (
            <div className="bg-card border border-border-light rounded-2xl overflow-hidden" data-testid="admin-counters-table">
              <div className="overflow-x-auto">
                <table className="w-full text-sm min-w-[560px]">
                  <thead className="bg-background text-xs text-text-muted uppercase tracking-wider">
                    <tr>
                      <th className="text-left px-5 py-3">Outlet</th>
                      <th className="text-left px-5 py-3">Counter</th>
                      <th className="text-left px-5 py-3">Status</th>
                      <th className="text-left px-5 py-3">Since</th>
                      <th className="text-right px-5 py-3">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border-light">
                    {rows.map((c) => {
                      const open = c.status === 'open';
                      return (
                        <tr key={c.id} data-testid={`admin-counter-row-${c.id}`} className="hover:bg-background/50">
                          <td className="px-5 py-3 text-text-secondary">{c.vendor_name}</td>
                          <td className="px-5 py-3 font-medium text-text-primary">{c.name}</td>
                          <td className="px-5 py-3">
                            <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full border ${open ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-700 border-red-200'}`}>
                              {open ? <DoorOpen className="h-3 w-3" /> : <DoorClosed className="h-3 w-3" />}{open ? 'Open' : 'Closed'}
                            </span>
                          </td>
                          <td className="px-5 py-3 text-text-muted text-xs">{fmtTime(open ? c.opened_at : c.closed_at)}</td>
                          <td className="px-5 py-3 text-right">
                            <button
                              data-testid={`admin-counter-toggle-${c.id}`}
                              onClick={() => toggle(c)}
                              disabled={busy === c.id}
                              className={`text-xs px-3 py-1.5 rounded-lg font-medium disabled:opacity-50 ${open ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-emerald-600 text-white hover:bg-emerald-700'}`}
                            >
                              {busy === c.id ? '…' : (open ? 'Close' : 'Open')}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default AdminCounters;
